import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { adminUserFetcher } from "components/adminUserApi";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !loading && !isAutoLoginInProgress) {
        setIsAutoLoginInProgress(true);
        afterLogin();
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate, loading, isAutoLoginInProgress]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await login();
  }

  const login = async () => {
    setError(null);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      await afterLogin();
    } catch (error: unknown) {
      if (!error || typeof error !== "object" || !("code" in error)) {
        setError("予期しないエラーが発生しました。");
        return;
      }
      let errorMessage = "ログインに失敗しました。";
      if (error.code === "auth/invalid-email") {
        errorMessage = "無効なメールアドレスです。";
      } else if (error.code === "auth/user-disabled") {
        errorMessage = "このアカウントは無効化されています。";
      } else if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password"
      ) {
        errorMessage = "メールアドレスまたはパスワードが間違っています。";
      }
      setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const afterLogin = async () => {
    setError(null);

    try {
      // Then check if the user exists in Firestore as an admin
      const adminUser = await adminUserFetcher();

      if (!adminUser) {
        // If the user doesn't exist in Firestore as an admin, sign them out
        await signOut(auth);
        setError("このユーザーは管理者として登録されていません。");
        return;
      }

      // If everything is fine, navigate to the users page
      navigate("/users");
    } catch (error: unknown) {
      // Check if it's a network error vs authorization error
      const errorMessage = error instanceof Error && error.message.includes('network')
        ? "ネットワークエラーが発生しました。"
        : "このユーザーは管理者として登録されていません。";
      setError(errorMessage);

      console.error(error);

      await signOut(auth);
    } finally {
      setIsAutoLoginInProgress(false);
    }
  }

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        mt: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        管理者ログイン
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        メールアドレスとパスワードを入力してください
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="パスワード"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "ログイン中..." : "ログイン"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
